import { useTranslation } from "react-i18next";
import { makeStyles, Typography } from "@material-ui/core";
import { formatTokens } from "../helpers";

import { AIModel, AIProvider } from "@dashboard-v3/api";

interface Props {
  provider: AIProvider;
  model?: AIModel;
  value: string;
}

export default function ModelHints({ provider, model, value }: Props) {
  const { t } = useTranslation("storage");
  const classes = useStyles();

  if (!value.trim()) return null;

  if (!model) {
    const customKey = provider.baseUrlRequired
      ? "externalAI.hints.customDeployment"
      : "externalAI.hints.customModel";

    return (
      <Typography
        variant="caption"
        color="textSecondary"
        className={classes.custom}
      >
        {t(customKey)}
      </Typography>
    );
  }

  const hints: { label: string; value: string }[] = [];

  if (model.contextTokens !== undefined) {
    hints.push({
      label: t("externalAI.hints.context"),
      value: t("externalAI.hints.tokens", {
        amount: formatTokens(model.contextTokens),
      }),
    });
  }

  if (model.outputTokens !== undefined) {
    hints.push({
      label: t("externalAI.hints.output"),
      value: t("externalAI.hints.tokens", {
        amount: formatTokens(model.outputTokens),
      }),
    });
  }

  hints.push({
    label: t("externalAI.hints.reasoning"),
    value: getReasoningHint(model, t),
  });

  return (
    <div className={classes.hints}>
      {hints.map(hint => (
        <span className={classes.hint} key={hint.label}>
          <Typography variant="caption" color="textSecondary">
            {hint.label}
          </Typography>
          <Typography variant="caption" className={classes.hintValue}>
            {hint.value}
          </Typography>
        </span>
      ))}
    </div>
  );
}

function getReasoningHint(model: AIModel, t: (key: string) => string) {
  if (model.reasoningOptions.length > 0) {
    return model.reasoningOptions.join(" · ");
  }

  if (model.reasoning) return t("externalAI.hints.reasoningNoLevels");

  return t("externalAI.hints.reasoningUnsupported");
}

const useStyles = makeStyles({
  hints: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  hint: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 6,
    padding: "3px 10px",
    borderRadius: 4,
    backgroundColor: "#f3f5f8",
  },
  hintValue: { fontWeight: 500 },
  custom: { display: "block", marginTop: 10 },
});
